import { ReactElement, useMemo, useState, useEffect, useRef } from 'react';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { ResolvedWeather, WeatherControllerDict } from '../../../types/Session';
import { ATMOSPHERIC_SOURCE_LABEL, AtmosphericSourceEnum } from '../../../types/Run';

export type WeatherPlotChannel = 'Temp_T' | 'Baro_P' | 'Humidity_x' | 'AirDensity';

interface WeatherTemperaturePlotProps {
  weatherControllerDict: WeatherControllerDict | null;
  selectedSource: string;
  channel?: WeatherPlotChannel;
  resolvedWeather?: ResolvedWeather | null;
  utcOffsetSeconds?: number;
  height?: number;
}

interface ChannelConfig {
  chartTitle: string;
  axisTitle: string;
  scale: number; // display value = raw value * scale
  decimals: number;
  unit: string;
}

const CHANNEL_CONFIG: Record<WeatherPlotChannel, ChannelConfig> = {
  Temp_T: { chartTitle: 'Temperature', axisTitle: 'Temperature (°C)', scale: 1, decimals: 1, unit: '°C' },
  Baro_P: { chartTitle: 'Barometric Pressure', axisTitle: 'Pressure (hPa)', scale: 0.01, decimals: 1, unit: 'hPa' },
  Humidity_x: { chartTitle: 'Relative Humidity', axisTitle: 'Humidity (%)', scale: 1, decimals: 0, unit: '%' },
  AirDensity: { chartTitle: 'Air Density (resolved)', axisTitle: 'Density (kg/m³)', scale: 1, decimals: 4, unit: 'kg/m³' },
};

const DENSITY_SERIES_COLOR = '#dc2626';

export default function WeatherTemperaturePlot({
  weatherControllerDict,
  selectedSource,
  channel = 'Temp_T',
  resolvedWeather = null,
  utcOffsetSeconds = 0,
  height = 384,
}: WeatherTemperaturePlotProps): ReactElement {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isReady, setIsReady] = useState(false);
  const channelConfig = CHANNEL_CONFIG[channel];

  // Wait for container to have valid dimensions before rendering chart
  useEffect(() => {
    if (containerRef.current) {
      const width = containerRef.current.offsetWidth;

      if (width > 0) {
        setIsReady(true);
      } else {
        // If width is 0, wait a bit and check again
        const timer = setTimeout(() => {
          if (containerRef.current && containerRef.current.offsetWidth > 0) {
            setIsReady(true);
          }
        }, 100);
        return () => clearTimeout(timer);
      }
    }
  }, []);

  const { series, hasData, colors, timeRange, syntheticPoints, sampleSources } = useMemo(() => {
    const empty = {
      series: [] as any[], hasData: false, colors: [] as string[],
      timeRange: null as { min: number; max: number } | null,
      syntheticPoints: [] as { seriesIndex: number; dataPointIndex: number }[],
      sampleSources: [] as ResolvedWeatherSampleSources[],
    };
    if (!weatherControllerDict) {
      return empty;
    }

    // Define color mapping for each source
    const sourceConfig = [
      { name: 'Weather Api', key: 'weather_api' as keyof WeatherControllerDict, color: '#3b82f6' }, // Blue
      { name: 'Probe', key: 'probe' as keyof WeatherControllerDict, color: '#10b981' }, // Green
      { name: 'Probe-API', key: 'probe_hybrid' as keyof WeatherControllerDict, color: '#06b6d4' }, // Cyan
      { name: 'Local Weather Station', key: 'local_station' as keyof WeatherControllerDict, color: '#f59e0b' }, // Amber
      { name: 'Manual', key: 'manual' as keyof WeatherControllerDict, color: '#8b5cf6' }, // Purple
      { name: 'Bike Computer', key: 'bike_computer' as keyof WeatherControllerDict, color: '#4b5563' }, // Slate
    ];

    const seriesData: any[] = [];
    const colorArray: string[] = [];
    let probeTimeRange: { min: number; max: number } | null = null;
    const twoHoursMs = 2 * 60 * 60 * 1000;

    // Per-source per-channel offsets from offsets_raw
    const offsets = weatherControllerDict.offsets_raw || {};
    const rawChannel = channel === 'AirDensity' ? null : channel;

    // Process each source (raw channels only; density is a single resolved series)
    for (const config of sourceConfig) {
      if (rawChannel === null) {
        break;
      }
      const weatherSource = weatherControllerDict[config.key] as any;

      // Skip if source has no data
      if (!weatherSource || !weatherSource.weather_data || weatherSource.weather_data.length === 0) {
        continue;
      }

      const sourceOffset = (offsets as any)[config.key]?.[rawChannel] ?? 0;

      // Filter out data points with null values and sort by time
      const validData = weatherSource.weather_data
        .filter((d: any) => d[rawChannel] !== null && d[rawChannel] !== undefined && d.TimeAbs_t !== null)
        .sort((a: any, b: any) => a.TimeAbs_t - b.TimeAbs_t);

      if (validData.length === 0) {
        continue;
      }

      // Convert Unix timestamps to Date objects and apply offset + display scale
      const data = validData.map((d: any) => ({
        x: (d.TimeAbs_t + utcOffsetSeconds) * 1000, // Apply UTC offset and convert to milliseconds
        y: ((d[rawChannel] as number) + sourceOffset) * channelConfig.scale,
      }));

      // Calculate time range for probe data
      if (config.key === 'probe' && data.length > 0) {
        probeTimeRange = {
          min: data[0].x - twoHoursMs,
          max: data[data.length - 1].x + twoHoursMs,
        };
      }

      // Highlight the selected source with thicker line
      const isSelected = config.name === selectedSource;
      seriesData.push({
        name: config.name,
        data: data,
        zIndex: isSelected ? 10 : 1, // Bring selected to front
      });
      colorArray.push(config.color);
    }

    // Air density: one server-resolved series (the values synthetic runs consume)
    const sources: ResolvedWeatherSampleSources[] = [];
    if (channel === 'AirDensity') {
      const samples = resolvedWeather?.samples ?? [];
      if (samples.length > 0) {
        const data = samples.map((s) => {
          sources.push(s.sources);
          return { x: (s.TimeAbs_t + utcOffsetSeconds) * 1000, y: s.AirDensity_d };
        });
        seriesData.push({ name: 'Air density (resolved)', data, zIndex: 10 });
        colorArray.push(DENSITY_SERIES_COLOR);
      }
      // Anchor the density window on the probe collection when it exists
      const probeData = weatherControllerDict.probe?.weather_data ?? [];
      const probeTimes = probeData
        .map((d) => d.TimeAbs_t)
        .filter((t): t is number => t !== null && t !== undefined)
        .sort((a, b) => a - b);
      if (probeTimes.length > 0) {
        probeTimeRange = {
          min: (probeTimes[0] + utcOffsetSeconds) * 1000 - twoHoursMs,
          max: (probeTimes[probeTimes.length - 1] + utcOffsetSeconds) * 1000 + twoHoursMs,
        };
      }
    }

    // The visible window anchors on the actual test (probe) data +/- 2 h; with
    // no probe data yet, it anchors on the manual points +/- 2 h instead.
    const manualSeries = seriesData.find((s) => s.name === 'Manual');
    const syntheticManualPoints: { seriesIndex: number; dataPointIndex: number }[] = [];
    let windowRange = probeTimeRange;
    if (!windowRange && manualSeries && manualSeries.data.length > 0) {
      windowRange = {
        min: manualSeries.data[0].x - twoHoursMs,
        max: manualSeries.data[manualSeries.data.length - 1].x + twoHoursMs,
      };
    }

    // Extend the Manual series flat to cover the window and every other
    // series' extent, mirroring the backend hold-first/hold-last
    // extrapolation — the flat line must span the test window even when the
    // manual point itself sits outside the visible range.
    if (manualSeries && manualSeries.data.length > 0) {
      const allX = seriesData.flatMap((s) => s.data.map((d: { x: number }) => d.x));
      if (windowRange) {
        allX.push(windowRange.min, windowRange.max);
      }
      const chartMin = Math.min(...allX);
      const chartMax = Math.max(...allX);
      const manualIndex = seriesData.indexOf(manualSeries);
      const first = manualSeries.data[0];
      const last = manualSeries.data[manualSeries.data.length - 1];
      if (chartMin < first.x) {
        manualSeries.data.unshift({ x: chartMin, y: first.y });
        syntheticManualPoints.push({ seriesIndex: manualIndex, dataPointIndex: 0 });
      }
      if (chartMax > last.x) {
        manualSeries.data.push({ x: chartMax, y: last.y });
        syntheticManualPoints.push({ seriesIndex: manualIndex, dataPointIndex: manualSeries.data.length - 1 });
      }
    }

    return {
      series: seriesData,
      hasData: seriesData.length > 0,
      colors: colorArray,
      timeRange: windowRange,
      syntheticPoints: syntheticManualPoints,
      sampleSources: sources,
    };
  }, [weatherControllerDict, selectedSource, utcOffsetSeconds, channel, resolvedWeather, channelConfig.scale]);

  if (!hasData) {
    return (
      <div className="cr-flex cr-h-64 cr-items-center cr-justify-center cr-rounded cr-bg-gray-50 cr-text-gray-500">
        {`No ${channelConfig.chartTitle.toLowerCase()} data available`}
      </div>
    );
  }

  // Calculate stroke widths - make selected source thicker
  const strokeWidths = series.map((s) => (s.name === selectedSource || channel === 'AirDensity' ? 3 : 2));

  const densityTooltipSuffix = (dataPointIndex: number): string => {
    const src = sampleSources[dataPointIndex];
    if (!src) {
      return '';
    }
    const label = (v: number) => ATMOSPHERIC_SOURCE_LABEL[v as AtmosphericSourceEnum] ?? String(v);
    return ` — T: ${label(src.temperature)}, P: ${label(src.barometric_pressure)}, RH: ${label(src.humidity)}`;
  };

  const options: ApexOptions = {
    chart: {
      type: 'line',
      height: 384,
      zoom: { enabled: true, allowMouseWheelZoom: false },
      toolbar: { show: true },
      animations: { enabled: false },
    },
    stroke: {
      curve: 'straight',
      width: strokeWidths,
    },
    colors: colors,
    xaxis: {
      type: 'datetime',
      ...(timeRange && {
        min: timeRange.min,
        max: timeRange.max,
      }),
      labels: {
        datetimeUTC: true,
        format: 'HH:mm',
      },
      title: {
        text: 'Time of Day',
        style: {
          fontSize: '12px',
          fontWeight: 600,
          color: '#374151',
        },
      },
    },
    yaxis: {
      title: {
        text: channelConfig.axisTitle,
        style: {
          fontSize: '12px',
          fontWeight: 600,
          color: '#374151',
        },
      },
      labels: {
        formatter: (val: number) => val.toFixed(channelConfig.decimals),
      },
    },
    grid: {
      borderColor: '#e5e7eb',
    },
    legend: {
      show: true,
      position: 'top',
      horizontalAlign: 'left',
      fontSize: '12px',
      fontWeight: 500,
      markers: {
        size: 6,
        strokeWidth: 2,
      },
      itemMargin: {
        horizontal: 10,
        vertical: 5,
      },
      onItemClick: {
        toggleDataSeries: true,
      },
      onItemHover: {
        highlightDataSeries: true,
      },
    },
    tooltip: {
      x: {
        format: 'HH:mm:ss',
      },
      y: {
        formatter: (val: number, opts?: any) => {
          const base = `${val.toFixed(channelConfig.decimals)} ${channelConfig.unit}`;
          if (channel === 'AirDensity' && opts) {
            return base + densityTooltipSuffix(opts.dataPointIndex);
          }
          return base;
        },
      },
      shared: true,
      intersect: false,
    },
    markers: {
      size: series.map((s) => (s.name === selectedSource ? 5 : 3)),
      strokeColors: '#fff',
      strokeWidth: 2,
      // Synthetic flat-extrapolation endpoints on the Manual series carry no
      // marker so only user-entered points read as data.
      discrete: syntheticPoints.map((p) => ({
        seriesIndex: p.seriesIndex,
        dataPointIndex: p.dataPointIndex,
        size: 0,
        fillColor: '#8b5cf6',
        strokeColor: '#fff',
      })),
      hover: {
        size: 7,
      },
    },
    // Left-aligned so the title clears the toolbar — see SessionCharts.tsx.
    title: {
      text: channelConfig.chartTitle,
      align: 'left',
    },
  };

  return (
    <div ref={containerRef} className="cr-rounded-xl cr-bg-clip-border cr-shadow-md cr-p-4" style={{ height: `${height}px` }}>
      {isReady ? (
        <Chart options={options} series={series} type="line" height="100%" />
      ) : (
        <div className="cr-flex cr-h-full cr-items-center cr-justify-center cr-rounded cr-bg-gray-50 cr-text-gray-500">
          Loading chart...
        </div>
      )}
    </div>
  );
}

interface ResolvedWeatherSampleSources {
  temperature: number;
  barometric_pressure: number;
  humidity: number;
}
